// TradingSessionContext owns the active account's complete trading session.
// Mount it once below WalletsProvider so every surface observes the exact same
// vault, portfolio and live-balance objects. The heavy execution machine is
// pair-route scoped in PairTradeSessionContext so TON signing stays lazy.

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { WalletPortfolio, WireHolding } from '../api/portfolio'
import type { UserlandWallet } from '../api/userland'
import { type UseAccountVault, useAccountVault } from '../hooks/useAccountVault'
import { useLimitOrders } from '../hooks/useLimitOrders'
import { useLiveTokenPrices } from '../hooks/useLiveTokenPrices'
import {
  type LiveWalletBalance,
  sumBalanceNanos,
  useLiveWalletBalance,
} from '../hooks/useLiveWalletBalance'
import { useSellFeeSettler } from '../hooks/useSellFeeSettler'
import { type UseWalletPortfolio, useWalletPortfolio } from '../hooks/useWalletPortfolio'
import { balanceRate } from '../lib/balanceLines'
import { type MarkAnchors, applyLivePricesToPortfolio } from '../lib/livePortfolioValue'
import { type LivePriceInput, getLivePrice, publishLivePrice } from '../lib/livePriceStore'
import { mergeWalletPortfolios } from '../lib/mergeWalletPortfolios'
import { openHoldingMasters, publishHoldingPairSpots } from '../lib/publishHoldingPairSpots'
import {
  markTradeLifecyclePaint,
  noteTradeLifecycleBalance,
  noteTradeLifecycleHoldings,
} from '../lib/tradeLifecycleTelemetry'
import { noteUnpricedHoldings } from '../lib/unpricedHoldingDiag'
import { useTradeRecipient, useWallets } from './WalletsContext'

export type TradingSessionContextValue = {
  tradeRecipient: UserlandWallet | null
  vault: UseAccountVault
  portfolio: UseWalletPortfolio
  liveBalance: LiveWalletBalance
  /** Sum of `is_selected` native balances. Header, popover and Spot read this. */
  selectedBalanceNano: string | null
  /** Merged holdings of `is_selected` wallets. Header USDT and Spot read this. */
  selectedPortfolio: WalletPortfolio | null
  /** Each `is_selected` wallet's own portfolio, live-repriced, before the
   *  merge (the read ones): Portfolio's History per wallet and a wallet tag's
   *  list (what that wallet holds of a token) read these (r9x-3). */
  selectedParts: WalletPortfolio[]
  gramUsdRate: number | null
  /** Re-read both canonical wallet read models through their shared instances. */
  reloadConfirmedData: () => void
  limitOrders: ReturnType<typeof useLimitOrders>
}

const Ctx = createContext<TradingSessionContextValue | null>(null)

/** Canon's price for one holding as a store input — its mark pool named, so
 *  only that pool's row takes it (review F3); null when canon has no price. */
export function canonLivePrice(
  holding: WireHolding,
  requestedAtMs: number | null
): LivePriceInput | null {
  // A drained pool's row prices THIS holding (what the pool pays for it), not
  // the token: never the store's price, which every screen and wallet shares.
  if (holding.mark_source === 'reserve') return null
  const priceTon = Number(holding.current_price_ton)
  if (!Number.isFinite(priceTon) || priceTon <= 0) return null
  const rawUsd = Number(holding.current_price_usd)
  return {
    priceTon,
    priceUsd: Number.isFinite(rawUsd) && rawUsd > 0 ? rawUsd : null,
    knownAsOfMs: requestedAtMs ?? undefined,
    ...(holding.mark_pool ? { pool: holding.mark_pool } : {}),
  }
}

/** "token pool" lines, sorted: each held row's mark pool (a stable memo key). */
function holdingMarkPools(...lists: (readonly WireHolding[] | undefined)[]): string[] {
  const out = new Set<string>()
  for (const list of lists) {
    for (const h of list ?? []) {
      if (h.mark_pool) out.add(`${h.token_master} ${h.mark_pool}`)
    }
  }
  return [...out].sort()
}

function SelectedBalanceSlot({
  address,
  onChange,
}: {
  address: string
  onChange: (address: string, nano: string | null) => void
}) {
  const { balanceNano } = useLiveWalletBalance(address)
  useEffect(() => {
    onChange(address, balanceNano)
  }, [address, balanceNano, onChange])
  return null
}

function SelectedPortfolioSlot({
  address,
  onChange,
}: {
  address: string
  onChange: (
    address: string,
    portfolio: WalletPortfolio | null,
    requestedAtMs: number | null
  ) => void
}) {
  const { portfolio, requestedAtMs } = useWalletPortfolio(address)
  useEffect(() => {
    onChange(address, portfolio, requestedAtMs)
  }, [address, onChange, portfolio, requestedAtMs])
  return null
}

export function TradingSessionProvider({ children }: { children: ReactNode }) {
  const tradeRecipient = useTradeRecipient()
  const { selected } = useWallets()
  const vault = useAccountVault()
  const address = tradeRecipient?.address ?? null
  const selectedAddresses = useMemo(() => selected.map((wallet) => wallet.address), [selected])
  const extraSelected = useMemo(
    () => selectedAddresses.filter((slotAddress) => slotAddress !== address),
    [address, selectedAddresses]
  )
  const [selectedMap, setSelectedMap] = useState<Record<string, string | null>>({})
  const [extraPortfolios, setExtraPortfolios] = useState<Record<string, WalletPortfolio | null>>({})
  // When each extra wallet's read was requested: an M2 anchor heard before it
  // yields to that snapshot (applyLivePricesToPortfolio snapshotAtMs).
  const [extraRequestedAt, setExtraRequestedAt] = useState<Record<string, number | null>>({})
  const onSelectedSlot = useCallback((slotAddress: string, nano: string | null) => {
    setSelectedMap((prev) => (prev[slotAddress] === nano ? prev : { ...prev, [slotAddress]: nano }))
  }, [])
  const onExtraPortfolio = useCallback(
    (slotAddress: string, nextPortfolio: WalletPortfolio | null, requestedAtMs: number | null) => {
      setExtraPortfolios((prev) =>
        prev[slotAddress] === nextPortfolio ? prev : { ...prev, [slotAddress]: nextPortfolio }
      )
      setExtraRequestedAt((prev) =>
        prev[slotAddress] === requestedAtMs ? prev : { ...prev, [slotAddress]: requestedAtMs }
      )
    },
    []
  )
  useEffect(() => {
    setExtraPortfolios((prev) => {
      let changed = false
      const next = { ...prev }
      for (const key of Object.keys(next)) {
        if (!extraSelected.includes(key)) {
          delete next[key]
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [extraSelected])
  const canonPortfolio = useWalletPortfolio(address)
  const liveBalance = useLiveWalletBalance(address)
  const limitOrders = useLimitOrders(tradeRecipient, vault)
  // An owed SELL 1% is retried from any page, not only the pair route that
  // sold: the server records it, this signs the retry once it is safe.
  useSellFeeSettler(tradeRecipient, liveBalance.balanceNano)
  // ONE GRAM/USD rate for the app (2026-11-04): the positions read's
  // ton_usd_rate — the rate the server states its GRAM totals at. The live
  // repricing, the header, the wallet and the Balance card all use it; a
  // client-derived rate (a top token's price_usd / last_price) was ~1% off.
  const gramUsdRate = balanceRate(canonPortfolio.portfolio)
  const selectedBalanceNano = useMemo(() => {
    if (selectedAddresses.length === 0) return liveBalance.balanceNano
    const summed = sumBalanceNanos(
      selectedAddresses.map((slotAddress) => selectedMap[slotAddress] ?? null)
    )
    if (summed !== null) return summed
    return liveBalance.balanceNano
  }, [liveBalance.balanceNano, selectedAddresses, selectedMap])
  const selectedRead = useMemo(() => {
    const parts: WalletPortfolio[] = []
    const requestedAt: (number | null)[] = [] // parallel to parts
    const primaryTicked = address != null && selectedAddresses.includes(address)
    if (primaryTicked && canonPortfolio.portfolio) {
      parts.push(canonPortfolio.portfolio)
      requestedAt.push(canonPortfolio.requestedAtMs)
    }
    for (const slotAddress of extraSelected) {
      const extra = extraPortfolios[slotAddress]
      if (extra) {
        parts.push(extra)
        requestedAt.push(extraRequestedAt[slotAddress] ?? null)
      }
    }
    // The merged view's snapshot is as new as its newest part.
    const known = requestedAt.filter((t): t is number => t !== null)
    const newest = known.length > 0 ? Math.max(...known) : null
    // how many wallets the view sums, read or not: a wallet still unread
    // must not be taken for one holding nothing (mergeWalletPortfolios)
    return { parts, requestedAt, newest, viewed: (primaryTicked ? 1 : 0) + extraSelected.length }
  }, [
    address,
    canonPortfolio.portfolio,
    canonPortfolio.requestedAtMs,
    extraPortfolios,
    extraRequestedAt,
    extraSelected,
    selectedAddresses,
  ])
  const selectedRaw = useMemo(
    () => mergeWalletPortfolios(selectedRead.parts, selectedRead.viewed),
    [selectedRead]
  )
  // Live prices for the tokens this wallet holds, from the app-wide tape.
  //
  // Every value and PnL field the portfolio endpoint returns is priced at
  // REQUEST time, and the only thing that refetched it was this wallet's own
  // balance push — which the backend emits on an applied transaction and never
  // on a price move. So the numbers froze at the last read until the wallet
  // itself traded. They are repriced HERE, above the router, because the same
  // object feeds the pair Rail and Instant Trade: fixing it on the Portfolio
  // screen alone would leave PnL stale everywhere else it is shown.
  const heldKey = openHoldingMasters(
    selectedRaw?.holdings ?? canonPortfolio.portfolio?.holdings
  ).join('\n')
  const heldTokens = useMemo(() => (heldKey.length === 0 ? [] : heldKey.split('\n')), [heldKey])
  // Each held token's mark pool: a USD₮-pool tick enters the shared store only
  // as one of these (Rail, Instant, watchlist and alerts read it by token).
  const markPoolKey = holdingMarkPools(
    canonPortfolio.portfolio?.holdings,
    selectedRaw?.holdings
  ).join('\n')
  const markPools = useMemo(
    () =>
      Object.fromEntries(
        markPoolKey
          .split('\n')
          .filter(Boolean)
          .map((l) => l.split(' '))
      ),
    [markPoolKey]
  )
  const livePrices = useLiveTokenPrices(heldTokens, gramUsdRate, markPools)
  // Canon carries a price for every holding, and it is a price source like any
  // other — publishing it is what makes a QUIET token agree across screens
  // instead of leaving each one on its own snapshot.
  //
  // Its knowledge is as of when the REQUEST went out, not when the answer
  // arrived. The difference is the whole post-trade window: a read issued
  // before the swap reached QuestDB comes back after the tape has already
  // delivered the tick, and stamping it with the arrival time would let that
  // pre-trade snapshot overwrite the price of the trade the operator just made.
  const canonHoldings = canonPortfolio.portfolio?.holdings
  const canonRequestedAtMs = canonPortfolio.requestedAtMs
  // The em-dash window is common to both screens, so it is measured here, on
  // CANON's answer — before the reprice below flips `price_available` wherever
  // the store helped and erases the distinction.
  useEffect(() => {
    noteUnpricedHoldings(canonHoldings)
  }, [canonHoldings])
  useEffect(() => {
    if (!canonHoldings) return
    for (const holding of canonHoldings) {
      const input = canonLivePrice(holding, canonRequestedAtMs)
      if (input) publishLivePrice(holding.token_master, input)
    }
  }, [canonHoldings, canonRequestedAtMs])
  // The pair page's USD is the number he compared. Asking the same endpoint
  // here means he does not have to open the pair to get it.
  useEffect(() => {
    // Retry only tokens that still lack a pair-page chain stamp. A refetch
    // used to restart the whole walk and cancel 67; now a landed GUTYA is
    // left alone and a failed/slow SFAILURE is asked again.
    const missing = openHoldingMasters(canonHoldings).filter(
      (token) => getLivePrice(token)?.chainAt === undefined
    )
    if (missing.length === 0) return
    void publishHoldingPairSpots(missing, undefined, publishLivePrice)
  }, [canonHoldings])
  // The last live price each row took (token × mark pool), the reference the
  // ×50 band judges the next tick against (review M2) — one map for every
  // portfolio repriced below, so the three agree. An anchor heard before a
  // portfolio's read was requested yields to that read.
  const markAnchors = useRef<MarkAnchors>(new Map()).current
  const portfolio = useMemo(
    () => ({
      ...canonPortfolio,
      portfolio: applyLivePricesToPortfolio(
        canonPortfolio.portfolio,
        livePrices,
        markAnchors,
        canonPortfolio.requestedAtMs
      ),
    }),
    [canonPortfolio, livePrices, markAnchors]
  )
  const selectedPortfolio = useMemo(
    () => applyLivePricesToPortfolio(selectedRaw, livePrices, markAnchors, selectedRead.newest),
    [livePrices, selectedRaw, markAnchors, selectedRead.newest]
  )
  const selectedParts = useMemo(
    () =>
      selectedRead.parts
        .map((part, i) =>
          applyLivePricesToPortfolio(part, livePrices, markAnchors, selectedRead.requestedAt[i])
        )
        .filter((part): part is WalletPortfolio => part !== null),
    [livePrices, selectedRead, markAnchors]
  )
  // Two of the three lanes a trade updates are owned here, so this is where
  // they are measured — at PAINT, because "all three land together" is a claim
  // about the frame the operator reads, not about when data arrived. Both are
  // no-ops unless a trade recording is open.
  const balanceNano = liveBalance.balanceNano
  useEffect(() => {
    if (noteTradeLifecycleBalance(balanceNano)) markTradeLifecyclePaint('balance_paint')
  }, [balanceNano])
  const portfolioValue = portfolio.portfolio
  useEffect(() => {
    if (!portfolioValue) return
    // Every applied portfolio answer is reported with the projector checkpoint
    // it carried. This lane is NOT polled — `git log -S setInterval` over this
    // file and the telemetry module is empty, and the comment that used to
    // claim a fixed schedule here was describing a poller that never existed.
    // What it actually has is a bounded post-EVENT sweep (RELOAD_DELAYS_MS),
    // so without the checkpoint "our read was early" and "the projector had
    // not written yet" look identical, and they have opposite fixes.
    const changed = noteTradeLifecycleHoldings({
      holdings: portfolioValue.holdings,
      eventID: portfolioValue.updated_through_wallet_event_id,
    })
    if (changed) markTradeLifecyclePaint('holdings_paint')
  }, [portfolioValue])
  const reloadConfirmedData = useMemo(
    () => () => {
      portfolio.reload()
      liveBalance.reload()
    },
    [liveBalance.reload, portfolio.reload]
  )

  const value = useMemo<TradingSessionContextValue>(
    () => ({
      tradeRecipient,
      vault,
      portfolio,
      liveBalance,
      selectedBalanceNano,
      selectedPortfolio,
      selectedParts,
      gramUsdRate,
      reloadConfirmedData,
      limitOrders,
    }),
    [
      gramUsdRate,
      limitOrders,
      liveBalance,
      portfolio,
      reloadConfirmedData,
      selectedBalanceNano,
      selectedPortfolio,
      selectedParts,
      tradeRecipient,
      vault,
    ]
  )

  return (
    <Ctx.Provider value={value}>
      {selectedAddresses.map((slotAddress) => (
        <SelectedBalanceSlot key={slotAddress} address={slotAddress} onChange={onSelectedSlot} />
      ))}
      {extraSelected.map((slotAddress) => (
        <SelectedPortfolioSlot
          key={`pf-${slotAddress}`}
          address={slotAddress}
          onChange={onExtraPortfolio}
        />
      ))}
      {children}
    </Ctx.Provider>
  )
}

export function useTradingSession(): TradingSessionContextValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useTradingSession must be used inside <TradingSessionProvider>')
  return ctx
}

export function useOptionalTradingSession(): TradingSessionContextValue | null {
  return useContext(Ctx)
}
